import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button, ButtonLink } from "../../misc/button/Button";
import { Spin, Alert } from "antd";
import SpinLoading from "../../misc/loading/SpinLoading";
import { DeclarationContext } from "../../context/DeclarationWrapper";
import { AuthContext } from "../../context/AuthProviderWrapper";
import { renderCheckboxOrHidden } from "../../helper-function/helperFunction";
import ZeroAmountConfirmationModal from "../../misc/ZeroAmountConfirmationModal";
import { useDeclarationForm } from "../../../hooks/useDeclarationForm";
import { useDateValidation } from "../../../hooks/useDateValidation";
import { useZeroAmountValidation } from "../../../hooks/useZeroAmountValidation";
import DeclarationFormTable from "../../common/DeclarationFormTable";
import axios from "axios";
import { BACKEND_URL } from "../../../config/config";
import { NotificationContext } from "../../context/NotificationWrapper";

function Salary(props) {
  const { isEdit, isAnnual = false, isSemiAnnual = false, semester = null, selectedYear: propSelectedYear = null } = props;
  const { user, activeCompany } = useContext(AuthContext);
  const navigate = useNavigate();
  const { updateDeclaration, updateDsnFile, availablePeriods } = useContext(DeclarationContext);
  const { openNotification } = useContext(NotificationContext);

  // State for zero amount modal
  const [showZeroAmountModal, setShowZeroAmountModal] = useState(false);
  const [validatedDeclarationData, setValidatedDeclarationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [conditionCheckbox, setConditionCheckbox] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [currentSemester, setCurrentSemester] = useState(semester || 1);
  const [isMonthlyInitialized, setIsMonthlyInitialized] = useState(false);
  const [isSemiAnnualInitialized, setIsSemiAnnualInitialized] = useState(false);
  const [isAnnualInitialized, setIsAnnualInitialized] = useState(false);

  // Initialize date validation hook
  const {
    selectedMonth,
    selectedYear,
    handleMonthChange,
    handleYearChange,
  } = useDateValidation();

  // Initialize declaration form hook with date from useDateValidation
  const {
    salary,
    taxes,
    errors,
    isInputLoading,
    handleSalaryChange,
    handleMoneyFieldChange,
    validateDeclaration,
    setErrors,
    setSalary,
    handleYearChange: handleFormYearChange,
  } = useDeclarationForm({
    initialSalary: {
      month: selectedMonth,
      // Pour les déclarations annuelles, year sera défini par le useEffect availableYears
      // Pour les déclarations semestrielles, utiliser propSelectedYear
      // Pour les déclarations mensuelles, utiliser selectedYear
      year: isAnnual ? null : (isSemiAnnual && propSelectedYear ? propSelectedYear.toString() : selectedYear),
    },
    membershipCode: activeCompany.membershipCode,
    autoCalculate: true,
    isAnnual: isAnnual,
  });

  // Initialize zero amount validation hook
  const {
    getValidationErrorMessage,
    isValidAttestation,
  } = useZeroAmountValidation();

  // Update salary when date changes
  useEffect(() => {
    // Pour les déclarations annuelles, NE PAS mettre à jour year depuis selectedYear
    // car year sera géré par le useEffect availableYears
    if (isAnnual) {
      setSalary(prev => ({
        ...prev,
        month: selectedMonth,
        // Garder prev.year pour les déclarations annuelles
      }));
    } else {
      // Pour les déclarations semestrielles, utiliser propSelectedYear
      // Pour les déclarations mensuelles, utiliser selectedYear
      setSalary(prev => ({
        ...prev,
        month: selectedMonth,
        year: isSemiAnnual && propSelectedYear ? propSelectedYear.toString() : selectedYear,
      }));
    }
  }, [selectedMonth, selectedYear, propSelectedYear, setSalary, isSemiAnnual, isAnnual]);

  // Helper pour trouver le mois disponible le plus récent (excluant mois actuel)
  const getMostRecentAvailableMonth = (availablePeriods) => {
    if (!availablePeriods?.monthly) return null;

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    const years = Object.keys(availablePeriods.monthly).sort((a, b) => b - a);

    for (const year of years) {
      const months = availablePeriods.monthly[year].sort((a, b) => b - a);
      for (const month of months) {
        if (parseInt(year) === currentYear && month === currentMonth) {
          continue;
        }
        return { month, year: parseInt(year) };
      }
    }

    return null;
  };

  // Helper pour trouver le semestre disponible le plus récent
  const getMostRecentAvailableSemester = (availablePeriods) => {
    if (!availablePeriods?.semiAnnual) return null;

    const years = Object.keys(availablePeriods.semiAnnual).sort((a, b) => b - a);

    for (const year of years) {
      const semesters = availablePeriods.semiAnnual[year].sort((a, b) => b - a);
      if (semesters.length > 0) {
        return { semester: semesters[0], year: parseInt(year) };
      }
    }

    return null;
  };

  /**
   * Vérifie si une année est complètement indisponible pour les déclarations mensuelles
   * Une année est indisponible si tous ses mois (passés et présents) sont déjà déclarés
   */
  const isYearCompletelyUnavailableForMonthly = (year) => {
    if (!availablePeriods?.monthly) return false;

    const yearStr = year.toString();
    const availableMonths = availablePeriods.monthly[yearStr] || [];

    // Si l'année n'a aucun mois disponible, elle est complètement indisponible
    if (availableMonths.length === 0) {
      return true;
    }

    return false;
  };

  /**
   * Vérifie si une année est complètement indisponible pour les déclarations semestrielles
   * Une année est indisponible si ses deux semestres (passés et présents) sont déjà déclarés
   */
  const isYearCompletelyUnavailableForSemiAnnual = (year) => {
    if (!availablePeriods?.semiAnnual) return false;

    const yearStr = year.toString();
    const availableSemesters = availablePeriods.semiAnnual[yearStr] || [];

    // Si l'année n'a aucun semestre disponible, elle est complètement indisponible
    if (availableSemesters.length === 0) {
      return true;
    }

    return false;
  };

  /**
   * Wrapper pour handleYearChange qui ajuste automatiquement le mois si nécessaire
   */
  const handleYearChangeWithMonthAdjustment = (newYear) => {
    // Appeler le handler original
    handleYearChange(newYear);

    // Pour les déclarations mensuelles, vérifier si le mois sélectionné est disponible
    if (!isAnnual && !isSemiAnnual && availablePeriods?.monthly) {
      const currentMonth = selectedMonth ? parseInt(selectedMonth) : null;

      if (currentMonth) {
        const isCurrentMonthAvailable = availablePeriods.monthly[newYear]?.includes(currentMonth);

        if (!isCurrentMonthAvailable) {
          // Trouver le mois disponible le plus récent pour cette année
          const availableMonths = availablePeriods.monthly[newYear] || [];
          if (availableMonths.length > 0) {
            const mostRecentMonth = Math.max(...availableMonths);
            handleMonthChange(mostRecentMonth.toString());
          }
        }
      }
    }
  };

  /**
   * Wrapper pour handleYearChange qui ajuste automatiquement le semestre si nécessaire
   */
  const handleYearChangeWithSemesterAdjustment = (newYear) => {
    // Appeler le handler original
    handleYearChange(newYear);

    // Pour les déclarations semestrielles, vérifier si le semestre sélectionné est disponible
    if (isSemiAnnual && availablePeriods?.semiAnnual) {
      const isCurrentSemesterAvailable = availablePeriods.semiAnnual[newYear]?.includes(currentSemester);

      if (!isCurrentSemesterAvailable) {
        // Trouver le semestre disponible le plus récent pour cette année
        const availableSemesters = availablePeriods.semiAnnual[newYear] || [];
        if (availableSemesters.length > 0) {
          const mostRecentSemester = Math.max(...availableSemesters);
          setCurrentSemester(mostRecentSemester);
        }
      }
    }
  };

  // Update condition checkbox based on user and salary
  useEffect(() => {
    const condition = renderCheckboxOrHidden(activeCompany, salary);
    setConditionCheckbox(condition);
  }, [activeCompany, salary]);

  // Fetch available years for annual declarations
  useEffect(() => {
    if (isAnnual && activeCompany?.membershipCode && !isAnnualInitialized) {
      axios
        .get(`${BACKEND_URL}/api/declaration/available-years/${activeCompany.membershipCode}`, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        })
        .then((response) => {
          setAvailableYears(response.data.data.availableYears);
          // Set default year to the most recent available year
          if (response.data.data.availableYears.length > 0) {
            setSalary((prevSalary) => ({
              ...prevSalary,
              year: response.data.data.availableYears[0].toString(),
            }));
          }
          setIsAnnualInitialized(true);
        })
        .catch((error) => {
          console.error("Error fetching available years:", error);
          openNotification("Erreur", "Impossible de récupérer les années disponibles", "error");
          setIsAnnualInitialized(true);
        });
    }
  }, [isAnnual, activeCompany?.membershipCode, isAnnualInitialized, openNotification]);

  // Set default values for monthly declarations using most recent available period
  useEffect(() => {
    if (!isAnnual && !isSemiAnnual && availablePeriods?.monthly && !isMonthlyInitialized) {
      const mostRecent = getMostRecentAvailableMonth(availablePeriods);

      if (mostRecent) {
        // Mettre à jour les sélecteurs - cela va déclencher le useEffect qui synchronise salary
        handleMonthChange(mostRecent.month.toString());
        handleYearChange(mostRecent.year.toString());

        // Marquer comme initialisé pour permettre l'affichage du formulaire
        setIsMonthlyInitialized(true);
      } else {
        // Si aucune période disponible, marquer quand même comme initialisé pour afficher le formulaire
        setIsMonthlyInitialized(true);
      }
    }
  }, [isAnnual, isSemiAnnual, availablePeriods, handleMonthChange, handleYearChange, isMonthlyInitialized]);

  // Set default values for semi-annual declarations using most recent available period
  useEffect(() => {
    if (isSemiAnnual && availablePeriods?.semiAnnual && !isSemiAnnualInitialized) {
      const mostRecent = getMostRecentAvailableSemester(availablePeriods);

      if (mostRecent) {
        // Mettre à jour l'année dans les sélecteurs - cela va déclencher le useEffect qui synchronise salary
        handleYearChange(mostRecent.year.toString());

        // Mettre à jour le semestre (pas géré par useDateValidation)
        setCurrentSemester(mostRecent.semester);

        // Marquer comme initialisé pour permettre l'affichage du formulaire
        setIsSemiAnnualInitialized(true);
      } else {
        // Si aucune période disponible, marquer quand même comme initialisé pour afficher le formulaire
        setIsSemiAnnualInitialized(true);
      }
    }
  }, [isSemiAnnual, availablePeriods, handleYearChange, isSemiAnnualInitialized]);

  /**
   * Check and submit handler with 4-case validation
   */
  const checkAndSubmit = (e) => {
    e.preventDefault();
    setErrors([]);

    // Create temporary declaration for validation
    const tempDeclaration = {
      feeAmount: taxes?.feeAmount || 0,
      workForce: salary?.workForce,
    };

    // Check for validation errors (Cases 3 & 4)
    const validationError = getValidationErrorMessage(tempDeclaration);
    if (validationError) {
      setErrors([validationError]);
      return;
    }

    // Check if this is an attestation (Case 1)
    if (isValidAttestation(tempDeclaration)) {
      validateBackendThenShowModal();
    } else {
      // Case 2: Normal declaration
      handleSubmit();
    }
  };

  /**
   * Validate backend first, then show modal for attestation
   */
  const validateBackendThenShowModal = async () => {
    setIsLoading(true);

    try {
      const result = await validateDeclaration({
        ...salary,
        membershipCode: activeCompany.membershipCode,
        isAnnual: isAnnual,
        semester: isSemiAnnual ? currentSemester : null,
      });

      if (result) {
        setValidatedDeclarationData(result);
        setShowZeroAmountModal(true);
      }
    } catch (error) {
      // Errors are already handled by the hook
      console.error("Validation error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle submission for normal declarations
   */
  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const result = await validateDeclaration({
        ...salary,
        membershipCode: activeCompany.membershipCode,
        isAnnual: isAnnual,
        semester: isSemiAnnual ? currentSemester : null,
      });

      if (result) {
        // Préserver isAnnual et semester lors de la mise à jour du contexte
        updateDeclaration({
          ...result,
          isAnnual: isAnnual,
          isSemiAnnual: isSemiAnnual,
          semester: isSemiAnnual ? currentSemester : null,
        });
        navigate("/declaration-summary", { state: { message: result } });
      }
    } catch (error) {
      // Errors are already handled by the hook
      console.error("Submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle zero amount modal confirmation
   */
  const handleZeroAmountConfirm = (dsnFile) => {
    setShowZeroAmountModal(false);
    updateDsnFile(dsnFile);

    if (validatedDeclarationData) {
      // Préserver isAnnual et semester lors de la mise à jour du contexte
      updateDeclaration({
        ...validatedDeclarationData,
        isAnnual: isAnnual,
        isSemiAnnual: isSemiAnnual,
        semester: isSemiAnnual ? currentSemester : null,
      });
      navigate("/declaration-summary", { state: { message: validatedDeclarationData } });
    }
  };

  /**
   * Handle zero amount modal cancellation
   */
  const handleZeroAmountCancel = () => {
    setShowZeroAmountModal(false);
    setValidatedDeclarationData(null);
  };

  /**
   * Handle semester change
   */
  const handleSemesterChange = (value) => {
    setCurrentSemester(value);
  };

  /**
   * Get current year as string
   */
  const getCurrentYearToString = () => {
    return new Date().getFullYear().toString();
  };

  // Error display component
  const errorDisplay = errors?.length > 0 && (
    <Alert
      type="error"
      message={errors.map((el, idx) => <p key={idx}>{el}</p>)}
      style={{ width: "780px", alignItems: "center" }}
    />
  );

  // Pour les déclarations annuelles, attendre que availableYears soit chargé
  if (isAnnual && availableYears.length === 0) {
    return (
      <div className="info-salary">
        <Spin tip="Chargement des années disponibles..." />
      </div>
    );
  }

  // Pour les déclarations semestrielles, attendre que les périodes soient chargées ET initialisées
  if (isSemiAnnual && (availablePeriods === null || !isSemiAnnualInitialized)) {
    return (
      <div className="info-salary">
        <Spin tip="Chargement des périodes disponibles..." />
      </div>
    );
  }

  // Pour les déclarations mensuelles, attendre que les périodes soient chargées ET initialisées
  if (!isAnnual && !isSemiAnnual && (availablePeriods === null || !isMonthlyInitialized)) {
    return (
      <div className="info-salary">
        <Spin tip="Chargement des périodes disponibles..." />
      </div>
    );
  }

  return (
    <>
      <Spin spinning={isLoading} fullscreen />
      <div className="info-salary">
        {errorDisplay}
        <form onSubmit={checkAndSubmit}>
          <DeclarationFormTable
            mode="create"
            formType="html"
            salary={salary}
            taxes={taxes}
            isInputLoading={isInputLoading}
            conditionCheckbox={conditionCheckbox}
            onSalaryChange={handleSalaryChange}
            onMoneyFieldChange={handleMoneyFieldChange}
            onMonthChange={handleMonthChange}
            onYearChange={
              isSemiAnnual
                ? handleYearChangeWithSemesterAdjustment
                : (!isAnnual ? handleYearChangeWithMonthAdjustment : handleFormYearChange)
            }
            onSemesterChange={handleSemesterChange}
            getCurrentYearToString={getCurrentYearToString}
            isAnnual={isAnnual}
            isSemiAnnual={isSemiAnnual}
            semester={currentSemester}
            availableYears={availableYears}
            isYearUnavailable={
              isSemiAnnual
                ? isYearCompletelyUnavailableForSemiAnnual
                : (!isAnnual ? isYearCompletelyUnavailableForMonthly : undefined)
            }
            feeRate={activeCompany?.feeRate || '0,4%'}
            laborUnion={activeCompany?.laborUnion}
          />

          {!isEdit && (
            <div className="button-container" data-testid="isEdit">
              <ButtonLink to="/choose-declaration-type" text="Retour">
                Retour
              </ButtonLink>
              <Button
                type="submit"
                children={isLoading ? <SpinLoading /> : "Valider"}
              />
            </div>
          )}
        </form>
      </div>

      <ZeroAmountConfirmationModal
        visible={showZeroAmountModal}
        onConfirm={handleZeroAmountConfirm}
        onCancel={handleZeroAmountCancel}
        month={salary.month}
        year={salary.year}
        isAnnual={isAnnual}
        isSemiAnnual={isSemiAnnual}
        semester={currentSemester}
      />
    </>
  );
}

export default Salary;
